import { Injectable, inject, Injector } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { Auth, authState, signInWithPopup, signInWithRedirect, signOut } from '@angular/fire/auth';
import { GoogleAuthProvider, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { Firestore, doc, docData, setDoc, collection, getDocs, query, where } from '@angular/fire/firestore';
import { of, ReplaySubject, BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { User, Administrator } from '../auth/user.model';

export enum AdminRole {
  NONE = '',
  REVIEWER = 'reviewer',
  FINANCE = 'financial_officer',
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$ = new ReplaySubject<User>(1);

  // 代理登入相關狀態
  private _canProxyLogin: boolean = false; // 當前使用者是否有代理登入權限
  private _proxyableEmployeeIds: string[] = []; // 可代理的員工 ID 列表
  isProxyMode$ = new BehaviorSubject<boolean>(false); // 是否正在代理模式

  // 權限狀態（在登入時載入，存在 service 中供所有 component 使用）
  private _isReviewer: boolean = false;
  private _isCommittee: boolean = false;
  private _isChairPerson: boolean = false;
  private _isStoreAdmin: boolean = false;
  private _permissionsLoaded: boolean = false;

  // 提供 getter 讓 component 可以同步取得權限
  get isReviewer(): boolean {
    return this._isReviewer;
  }

  get isCommittee(): boolean {
    return this._isCommittee;
  }

  get isChairPerson(): boolean {
    return this._isChairPerson;
  }

  get isStoreAdmin(): boolean {
    return this._isStoreAdmin;
  }

  get permissionsLoaded(): boolean {
    return this._permissionsLoaded;
  }

  get canProxyLogin(): boolean {
    return this._canProxyLogin;
  }

  get proxyableEmployeeIds(): string[] {
    return this._proxyableEmployeeIds;
  }

  constructor(
    private auth: Auth,
    private fs: Firestore,
    private platform: Platform,
    private injector: Injector
  ) {
    setPersistence(this.auth, browserLocalPersistence).catch(err => {
      console.error('Explicit local persistence configuration failed:', err);
    });
    authState(this.auth)
      .pipe(
        switchMap((user) => {
          if (!user) {
            return of(null);
          }
          if (!user.email?.endsWith('@maria.org.tw')) {
            return of(null);
          }
          this.updateUserData(user);
          const userRef = doc(this.fs, `users/${user.uid}`);
          return docData(userRef) as any;
        })
      )
      .subscribe(async (user: User) => {
        if (user) {
          // 載入權限
          await this.loadUserPermissions(user.code);
        } else {
          // 登出時清空權限
          this.clearPermissions();
        }
        this.user$.next(user);
      });
  }

  /**
   * 載入使用者權限（只在登入時執行一次）
   */
  private async loadUserPermissions(employeeCode: string): Promise<void> {
    const now = new Date();
    const currentYear = now.getFullYear() - 1911;

    try {
      // 延遲載入 ShelterEmployeeProxyService 避免循環依賴
      const { ShelterEmployeeProxyService } = await import('./shelter-employee-proxy.service');
      const proxyService = this.injector.get(ShelterEmployeeProxyService);

      const [isReviewer, isCurrentYearCommittee, isLastYearCommittee, chairRole, proxyIds] = await Promise.all([
        this.everBeenReviewer(employeeCode),
        this.isAComittee(currentYear, employeeCode),
        this.isAComittee(currentYear - 1, employeeCode),
        this.getCommitteeRole(currentYear, employeeCode),
        proxyService.getProxyableEmployeeIds(employeeCode)
      ]);

      this._isReviewer = isReviewer;
      this._isStoreAdmin = isReviewer; // Store admin 目前與 reviewer 相同
      this._isCommittee = isCurrentYearCommittee || isLastYearCommittee;
      this._isChairPerson = chairRole === 'chairperson' || chairRole === 'vice_chairperson';

      // 載入代理登入權限
      this._canProxyLogin = proxyIds.length > 0;
      this._proxyableEmployeeIds = proxyIds;

      this._permissionsLoaded = true;
    } catch (error) {
      console.error('載入權限時發生錯誤:', error);
      this.clearPermissions();
    }
  }

  /**
   * 清空權限（登出時）
   */
  private clearPermissions(): void {
    this._isReviewer = false;
    this._isCommittee = false;
    this._isChairPerson = false;
    this._isStoreAdmin = false;
    this._canProxyLogin = false;
    this._proxyableEmployeeIds = [];
    this._permissionsLoaded = false;
    this.isProxyMode$.next(false);
  }

  googleSignIn() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    signInWithPopup(this.auth, provider);
  }



  private updateUserData(user): Promise<void> {
    const userRef = doc(this.fs, `users/${user.uid}`);

    var providerData = {
      uid: '',
      displayName: '',
      email: null,
      phoneNumber: null,
      providerId: '',
    };

    if (user.providerData.length > 0) {
      const _providerData = user.providerData[0];
      providerData = {
        uid: _providerData.uid,
        displayName: _providerData.displayName,
        email: _providerData.email,
        phoneNumber: _providerData.phoneNumber,
        providerId: _providerData.providerId,
      };
    }

    var empIdNumStr = providerData.email.substring(0, providerData.email.indexOf('@'));
    if (
      providerData.email &&
      providerData.email.startsWith('m') &&
      providerData.email.endsWith('@maria.org.tw')
    ) {
      empIdNumStr = providerData.email.substring(1, providerData.email.indexOf('@maria.org.tw'));
      while (empIdNumStr.length < 5) {
        empIdNumStr = '0' + empIdNumStr;
      }
    }
    const data = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      providerData: providerData,
      code: empIdNumStr,
    };

  return setDoc(userRef, data, { merge: true });
  }

  everBeenReviewer(employeeId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const q = query(collection(this.fs, 'administrators'), where('employeeId', '==', employeeId));
      getDocs(q)
        .then((snapshot) => {
          resolve(!snapshot.empty);
        })
        .catch((error) => {
          console.error(error);
          reject(error);
        });
    });
  }

  isAComittee(nationalYear: number, employeeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const q = query(
        collection(this.fs, 'comitteeMembers'),
        where('year', '==', nationalYear),
        where('employeeId', '==', employeeId)
      );
      getDocs(q)
        .then((snapshot) => {
          if (!snapshot.empty) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private getCommitteeRole(nationalYear: number, employeeId: string): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const q = query(
        collection(this.fs, 'comitteeMembers'),
        where('year', '==', nationalYear),
        where('employeeId', '==', employeeId)
      );
      getDocs(q)
        .then((snapshot) => {
          if (!snapshot.empty) {
            const data = snapshot.docs[0].data();
            resolve(data['role'] || null);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  adminRole(nationalYear: number, employeeId: string): Promise<AdminRole> {
    return new Promise((resolve, reject) => {
      const q = query(
        collection(this.fs, 'administrators'),
        where('employeeId', '==', employeeId),
        where('nationalYear', '==', nationalYear)
      );
      getDocs(q)
        .then((snapshot) => {
          if (!snapshot.empty) {
            let adminRoleDoc = snapshot.docs[0];
            const indexOfRole = Object.values(AdminRole).indexOf(
              (adminRoleDoc.data() as any).role as unknown as AdminRole
            );
            var adminRole : AdminRole = AdminRole[Object.keys(AdminRole)[indexOfRole]];
            resolve(adminRole);
          } else {
            resolve(AdminRole.NONE);
          }
        })
        .catch((error) => {
          console.error(error);
          reject(error);
        });
    });
  }

  signOut(): Promise<void> {
    return signOut(this.auth);
  }

  /**
   * 切換為代理登入模式
   * @param shelterEmployeeId 要代理的庇護員工編號
   */
  async switchToProxyLogin(shelterEmployeeId: string): Promise<void> {
    try {
      const currentUser = await new Promise<User>((resolve) => {
        this.user$.subscribe((user) => resolve(user)).unsubscribe();
      });

      if (!currentUser || !currentUser.uid) {
        throw new Error('使用者未登入');
      }

      const originalEmployeeId = currentUser.code;

      // 延遲載入 services 避免循環依賴
      const { ShelterEmployeeProxyService } = await import('./shelter-employee-proxy.service');
      const { EmployeeService } = await import('./employee.service');
      const proxyService = this.injector.get(ShelterEmployeeProxyService);
      const employeeService = this.injector.get(EmployeeService);

      // 檢查代理權限
      const canProxy = await proxyService.canProxyLogin(
        originalEmployeeId!,
        shelterEmployeeId
      );

      if (!canProxy) {
        throw new Error('無權限代理此員工');
      }

      // 取得庇護員工資料
      const shelterEmployee = await new Promise<any>((resolve, reject) => {
        employeeService.getEmployee(shelterEmployeeId).subscribe({
          next: (emp) => resolve(emp),
          error: (err) => reject(err),
        });
      });

      if (!shelterEmployee) {
        throw new Error('找不到庇護員工資料');
      }

      // 更新 Firestore user 文件，設定為代理模式
      const userRef = doc(this.fs, `users/${currentUser.uid}`);
      await setDoc(
        userRef,
        {
          code: shelterEmployeeId, // code 存放當前身分 (庇護員工)
          displayName: shelterEmployee.name,
          originalEmployeeId: originalEmployeeId, // 保留實際登入者
          isProxyLogin: true,
        },
        { merge: true }
      );

      this.isProxyMode$.next(true);
      console.log(`已切換為代理模式：${originalEmployeeId} -> ${shelterEmployeeId}`);
    } catch (error) {
      console.error('切換代理模式失敗:', error);
      throw error;
    }
  }

  /**
   * 退出代理模式，恢復為原本使用者
   */
  async exitProxyLogin(): Promise<void> {
    try {
      const currentUser = await new Promise<User>((resolve) => {
        this.user$.subscribe((user) => resolve(user)).unsubscribe();
      });

      if (!currentUser || !currentUser.uid || !currentUser.originalEmployeeId) {
        throw new Error('未在代理模式');
      }

      const originalEmployeeId = currentUser.originalEmployeeId;

      // 延遲載入 EmployeeService 避免循環依賴
      const { EmployeeService } = await import('./employee.service');
      const employeeService = this.injector.get(EmployeeService);

      // 取得原始員工資料
      const originalEmployee = await new Promise<any>((resolve, reject) => {
        employeeService.getEmployee(originalEmployeeId).subscribe({
          next: (emp) => resolve(emp),
          error: (err) => reject(err),
        });
      });

      if (!originalEmployee) {
        throw new Error('找不到原始員工資料');
      }

      // 恢復 Firestore user 文件
      const userRef = doc(this.fs, `users/${currentUser.uid}`);
      await setDoc(
        userRef,
        {
          code: originalEmployeeId, // 恢復為原本的 employeeId
          displayName: originalEmployee.name,
          originalEmployeeId: null,
          isProxyLogin: false,
        },
        { merge: true }
      );

      this.isProxyMode$.next(false);
      console.log(`已退出代理模式，恢復為：${originalEmployeeId}`);
    } catch (error) {
      console.error('退出代理模式失敗:', error);
      throw error;
    }
  }
}
